/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { EasFlowEvent, EasLogger, Log } from "../../model/logging";
import { DiagnosticSeverity } from "../../model/types";
import { EamContentAvType } from "../../model/pushNotification";
import type { IEamListener, IEamModel } from "../../model/pushNotification";
import { selectEasDisplayMode } from "../branding";
import {
  hideEasOsd,
  selectIsOsdSuspended,
  selectIsOsdVisible,
  selectOsdEasCountry,
  showEasOsd
} from "../osd";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";

/**
 * Bridge between the model-layer `EamModelImpl` and the Redux `osd` slice.
 *
 * `EamModelImpl` cannot dispatch: `src/model/` importing `src/viewModel/` is a
 * zero-tolerance layering violation (docs/MVVM-LAYERING-GATE.md, rule
 * `model-to-upper`). So it emits `IEamListener` callbacks and this bridge —
 * which sits on the ViewModel side of the arrow and may import both — turns
 * them into OSD actions. Same arrangement as `TsriEndpointRecoveryBridge` and
 * `NetworkAvailabilityBridge`.
 *
 * Until this bridge exists nothing implements `IEamListener`, so
 * `EamModelImpl.notifyEamDisplay()` logs "No listeners registered — alert not
 * displayed" and a received alert never reaches the screen. Registering it is
 * what makes the EAS path end-to-end.
 *
 * Presentation only. Preempting playback, playing the alert media, blocking
 * input and restoring the displaced content are all owned by the model layer,
 * where they can be driven by the alert's own lifecycle rather than by
 * whatever the UI happens to have rendered. This bridge shows the alert and
 * hides it again; the route the alert interrupted is put back by the
 * `easRouteRestore` listeners, which key off the same two actions.
 */
class EamBridge implements IEamListener {
  private static readonly TAG: string = "EamBridge";

  /** `eamDone` reason used when the OSD went away behind the model's back. */
  private static readonly DISMISS_REASON_OSD_CLEARED: string = "osdCleared";

  /** The EAS model this bridge observes. */
  private readonly mModel: IEamModel;

  /** Store dispatch, used to put the alert on and off the `osd` slice. */
  private readonly mDispatch: UnityAppDispatch;

  /** Store reader, used for the EAS country and the OSD backstop. */
  private readonly mGetState: () => UnityRootState;

  /** Whether `start()` has run and `stop()` has not. */
  private mIsActive: boolean = false;

  /** Unsubscribes the store watcher; `null` while the bridge is stopped. */
  private mUnsubscribe: (() => void) | null = null;

  private readonly mSubscribe: ((listener: () => void) => () => void) | null;

  /**
   * Build the bridge. Nothing is observed until {@link start} is called.
   *
   * @param model - The model-layer EAS model to observe
   * @param dispatch - The Redux store dispatch function
   * @param getState - Reader for the Redux state, used for the EAS country
   * @param subscribe - Store subscribe function, used to notice the OSD going
   *   away behind the model's back. Optional; without it the bridge still
   *   works, it simply loses that backstop.
   */
  constructor(
    model: IEamModel,
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState,
    subscribe?: (listener: () => void) => () => void
  ) {
    this.mModel = model;
    this.mDispatch = dispatch;
    this.mGetState = getState;
    this.mSubscribe = subscribe !== undefined ? subscribe : null;
  }

  /**
   * Whether an EAS alert is on the shared `osd` slice right now.
   *
   * Suspension counts as on screen: the alert is hidden but outstanding, and
   * `resumeOsd()` will bring it back.
   *
   * @returns True while the EAS OSD is showing or suspended
   */
  private isEasOsdOnScreen(): boolean {
    const state: UnityRootState = this.mGetState();
    return (
      selectOsdEasCountry(state).length > 0 &&
      (selectIsOsdVisible(state) || selectIsOsdSuspended(state))
    );
  }

  /**
   * Finish the alert if the OSD has gone while the model still holds it open.
   *
   * The model owns the alert's lifecycle, and everything that ends an alert is
   * supposed to route through `eamDone()` — which stops the alert media,
   * releases the input gate and restores the viewer's playback. Anything that
   * clears the shared `osd` slice without doing so leaves the model believing
   * an alert is still up: its audio keeps playing and every key stays dead,
   * over whatever screen the viewer has been returned to.
   *
   * Rather than chase each producer that might clear the slice, this makes the
   * OSD authoritative. The alert cannot outlive its own display.
   *
   * Cannot recurse: `eamDone()` hides the OSD, and by the time that lands the
   * model no longer reports an active alert.
   */
  private onStoreChanged(): void {
    if (!this.mModel.isAlertActive() || this.isEasOsdOnScreen()) {
      return;
    }
    Log.w(
      EamBridge.TAG,
      "onStoreChanged() EAS OSD cleared without finishing the alert — " +
        "ending it so its audio and input block cannot outlive it"
    );
    // A warning, not info: reaching here means something cleared the shared
    // slice without routing through the model, which is a bug in whatever did
    // it. The backstop makes it survivable, and this is how it gets found.
    EasLogger.logFlowEvent(
      EasFlowEvent.AlertFinished,
      "EAS OSD was cleared behind the model's back — backstop ended the alert",
      DiagnosticSeverity.Warning,
      undefined,
      { reason: EamBridge.DISMISS_REASON_OSD_CLEARED }
    );
    this.mModel.eamDone(EamBridge.DISMISS_REASON_OSD_CLEARED, Date.now());
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────

  /**
   * Begin relaying alerts to the `osd` slice.
   *
   * Registers with the model and, when a `subscribe` was supplied, starts
   * watching the store for an OSD cleared behind the model's back. A second
   * call is ignored and logged.
   */
  public start(): void {
    if (this.mIsActive) {
      Log.w(EamBridge.TAG, "start() bridge is already active — ignoring");
      return;
    }
    Log.i(EamBridge.TAG, "start() subscribing to EamModelImpl");
    this.mModel.addEamListener(this);
    if (this.mSubscribe !== null) {
      this.mUnsubscribe = this.mSubscribe((): void => {
        this.onStoreChanged();
      });
    }
    this.mIsActive = true;
  }

  /**
   * Stop relaying alerts and release both subscriptions.
   *
   * A call before {@link start} is ignored and logged.
   */
  public stop(): void {
    if (!this.mIsActive) {
      Log.w(EamBridge.TAG, "stop() bridge is not active — ignoring");
      return;
    }
    Log.i(EamBridge.TAG, "stop() unsubscribing from EamModelImpl");
    this.mModel.removeEamListener(this);
    if (this.mUnsubscribe !== null) {
      this.mUnsubscribe();
      this.mUnsubscribe = null;
    }
    this.mIsActive = false;
  }

  // ──────────────────────────────────────────────────────────────────
  // IEamListener
  // ──────────────────────────────────────────────────────────────────

  /**
   * Put the alert on screen.
   *
   * Routed through `showEasOsd` rather than `showOsd` so the request is fixed
   * at `EAS_OSD_PRIORITY` — the top of the shared ladder — and so
   * `OsdContainer` renders the country-specific `EasOsd` template instead of
   * the generic one.
   *
   * `contentAvType` is the one field consumed here rather than in the model:
   * whether the alert carries a picture is a question about how it is
   * presented, not about its lifecycle, and the answer has to reach the view
   * so the backdrop can clear and the media element can be given a surface.
   *
   * `isDismissible` is forwarded as well, and for the same reason: whether
   * the viewer may dismiss the alert is something the OSD has to say out
   * loud, in the "Press BACK to dismiss" footer. It arrives already resolved
   * by `EasDismissPolicy` and is the very value `EasInputGate` was opened
   * with, so the footer and the key are one decision rendered twice. They
   * used to be two — the footer came from a hardcoded per-country table —
   * which let the OSD promise a dismissal the gate refused, and hide one it
   * allowed.
   *
   * `mediaUrl` and `deliveredTimeMs` are deliberately not forwarded — each is
   * already acted on in the model, where it can be driven by the alert's own
   * lifecycle rather than by whatever the UI happens to have rendered.
   * `mediaUrl` is played by `EamModelImpl.beginAlertMedia()`, whose
   * end-of-media event is also what ends the alert. They stay in the
   * signature because the interface is shared.
   *
   * @param message - Sanitised alert text; "" when the alert carries none
   * @param isDismissible - Whether the viewer may dismiss the alert, already
   *   resolved against the market's template
   * @param mediaUrl - Alert media URL; "" when the alert carries none
   * @param contentAvType - Audio/video nature of the alert media
   * @param deliveredTimeMs - Delivery time in epoch milliseconds
   */
  public onDisplayEam(
    message: string,
    isDismissible: boolean,
    mediaUrl: string,
    contentAvType: EamContentAvType,
    deliveredTimeMs: number
  ): void {
    const country: string = selectEasDisplayMode(this.mGetState());
    const hasVideo: boolean =
      contentAvType === EamContentAvType.AUDIO_VIDEO ||
      contentAvType === EamContentAvType.VIDEO_ONLY;

    Log.i(
      EamBridge.TAG,
      `onDisplayEam() country=${country}, textLength=${String(message.length)}, ` +
        `isDismissible=${String(isDismissible)}, ` +
        `contentAvType=${contentAvType}, hasVideo=${String(hasVideo)}, ` +
        `mediaUrl=${EasLogger.sanitizeUrl(mediaUrl)}, ` +
        `deliveredTimeMs=${String(deliveredTimeMs)}, ` +
        `text="${message}"`
    );

    this.mDispatch(
      showEasOsd({
        message: message,
        // The EAS OSD's corner id slot. `onDisplayEam` carries no alert
        // identifier — `EasAlert` has no field the OSD spec maps to it — so
        // the slot is left empty and `EasOsd` omits it rather than showing a
        // borrowed value such as the MRPC request id.
        id: "",
        country: country,
        hasVideo: hasVideo,
        isDismissible: isDismissible
      })
    );
  }

  /** The alert finished normally. */
  public onDoneReadingEam(): void {
    Log.i(EamBridge.TAG, "onDoneReadingEam() dismissing EAS OSD");
    this.mDispatch(hideEasOsd());
  }

  /**
   * The alert was torn down without completing.
   *
   * Dismissed the same way as a normal finish: either way the alert is gone
   * and leaving it on screen would misrepresent a live emergency.
   */
  public onCloseEam(): void {
    Log.i(EamBridge.TAG, "onCloseEam() dismissing EAS OSD");
    this.mDispatch(hideEasOsd());
  }
}

export { EamBridge };
